export const CELL_COUNT = 9;
export const MAX_CELL_TEXT_LENGTH = 120;
export const FONT_FAMILIES = ["sans", "rounded", "serif", "mono"] as const;
export const FONT_SCALES = [75, 90, 100, 115, 130] as const;
export type BingoFontFamily = (typeof FONT_FAMILIES)[number];
export type BingoFontScale = (typeof FONT_SCALES)[number];
export const BOARD_ID_PATTERN = /^[A-Za-z0-9_-]{32}$/;
export const ADMIN_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;

export type BingoCell = {
  id: number;
  text: string;
  completed: boolean;
  fontFamily: BingoFontFamily;
  fontScale: BingoFontScale;
};

export type BingoBoardState = {
  boardId: string;
  revision: number;
  cells: BingoCell[];
  updatedAt: string;
};

export type StateReason = "initial" | "reconnect" | "update";

export type BoardChange =
  | { type: "text"; cellId: number }
  | { type: "style"; cellId: number }
  | { type: "completion"; cellId: number; completed: boolean }
  | { type: "reset_completions" }
  | { type: "clear_board" };

export type MutationMessage =
  | {
      type: "update_cell_text";
      cellId: number;
      text: string;
      expectedRevision: number;
      mutationId: string;
    }
  | {
      type: "set_cell_completed";
      cellId: number;
      completed: boolean;
      expectedRevision: number;
      mutationId: string;
    }
  | {
      type: "update_cell_style";
      cellId: number;
      fontFamily: BingoFontFamily;
      fontScale: BingoFontScale;
      expectedRevision: number;
      mutationId: string;
    }
  | {
      type: "reset_completions";
      expectedRevision: number;
      mutationId: string;
    }
  | {
      type: "clear_board";
      expectedRevision: number;
      mutationId: string;
    };

export type ClientMessage =
  | { type: "authenticate"; token: string }
  | { type: "request_state"; reason: "initial" | "reconnect" }
  | { type: "ping" }
  | MutationMessage;

export type ServerMessage =
  | { type: "authenticated" }
  | { type: "pong" }
  | {
      type: "state";
      state: BingoBoardState;
      reason: StateReason;
      mutationId?: string;
      change?: BoardChange;
    }
  | {
      type: "error";
      code: string;
      message: string;
      mutationId?: string;
      state?: BingoBoardState;
    };

export type CreateBoardResponse = {
  boardId: string;
  adminToken: string;
};

export function createEmptyBoard(boardId: string, now = new Date().toISOString()): BingoBoardState {
  return {
    boardId,
    revision: 0,
    cells: Array.from({ length: CELL_COUNT }, (_, id) => ({
      id,
      text: "",
      completed: false,
      fontFamily: "sans",
      fontScale: 100,
    })),
    updatedAt: now,
  };
}

export function isBoardId(value: string): boolean {
  return BOARD_ID_PATTERN.test(value);
}
